import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { withSite } from "@/lib/site";
import { TeamIdentity } from "@/components/TeamIdentity";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, fmtDateTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PhotoUpload from "@/components/PhotoUpload";
import { BatchEditor } from "@/lib/batch/BatchEditor";
import { reconcile, markDuplicates } from "@/lib/batch/batchCore";
import { toast } from "sonner";
import { Plus, SprayCan, CheckCircle2, Clock, Camera, Layers, User, Pencil, Loader2, Sparkles, Trash2 } from "lucide-react";
import { fetchPhotoRelevance, verdictUi } from "@/lib/photoRelevance";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const UNASSIGNED = "__unassigned__";

// Parse a free-text weekdays cell ("Mon,Tue" / "mon tue" / "1,2") into day indices 0..6.
function parseWeekdays(text) {
  return String(text || "").split(/[\s,]+/).map((t) => t.trim().toLowerCase()).filter(Boolean)
    .map((t) => {
      const i = WEEKDAYS.findIndex((d) => d.toLowerCase() === t || d.toLowerCase().startsWith(t.slice(0, 3)));
      if (i >= 0) return i;
      const n = Number(t);
      return Number.isInteger(n) && n >= 0 && n <= 6 ? n : null;
    }).filter((x) => x !== null);
}

export default function Cleaning() {
  const { has } = useAuth();
  const [today, setToday] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [log, setLog] = useState([]);
  const [people, setPeople] = useState([]);
  const [mine, setMine] = useState({ linked: false, instances: [], tasks: [] });
  const [modal, setModal] = useState(false);      // create
  const [editTask, setEditTask] = useState(null); // edit / reassign
  const [batch, setBatch] = useState(false);
  const [complete, setComplete] = useState(null);
  const [assigneeFilter, setAssigneeFilter] = useState("all");

  const load = async () => {
    const reqs = [
      api.get(withSite("/cleaning/today")),
      api.get(withSite("/cleaning/tasks")),
      api.get(withSite("/cleaning/log")),
      api.get("/cleaning/my-tasks"),
    ];
    if (has("manage_cleaning")) reqs.push(api.get(withSite("/cleaning/assignable-people")));
    const [t, ts, l, mt, pp] = await Promise.all(reqs);
    setToday(t.data); setTasks(ts.data); setLog(l.data); setMine(mt.data);
    if (pp) setPeople(pp.data);
  };
  useEffect(() => { load(); const h = () => load(); window.addEventListener("cc-site-changed", h); return () => window.removeEventListener("cc-site-changed", h); }, []);

  const matchesAssignee = (t) =>
    assigneeFilter === "all" ||
    (assigneeFilter === UNASSIGNED ? !t.assigned_to_person_id : t.assigned_to_person_id === assigneeFilter);
  const byFreq = (f) => tasks.filter((t) => t.frequency === f && t.active && matchesAssignee(t));

  return (
    <div>
      <PageHeader title="Cleaning Schedules" subtitle="Daily, weekly and monthly tasks with photo verification." testid="cleaning-header"
        action={has("manage_cleaning") && (
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-xl" data-testid="batch-add-cleaning-btn" onClick={() => setBatch(true)}><Layers size={18} className="mr-1" /> Batch add</Button>
            <Button className="bg-brand hover:bg-brand-hover rounded-xl" data-testid="add-cleaning-btn" onClick={() => setModal(true)}><Plus size={18} className="mr-1" /> New task</Button>
          </div>
        )} />

      <Tabs defaultValue={mine.linked ? "mine" : "today"}>
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="today" data-testid="tab-today">To do today</TabsTrigger>
          {mine.linked && <TabsTrigger value="mine" data-testid="tab-my-tasks">My cleaning tasks</TabsTrigger>}
          <TabsTrigger value="daily" data-testid="tab-daily">Daily</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">Weekly</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">Monthly</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">View log</TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="space-y-2">
          {today.length === 0 ? <EmptyState icon={CheckCircle2} title="You're all caught up." message="No cleaning tasks due today." /> :
            today.map((c) => (
              <div key={c.instance_id} className="cc-card p-4 flex items-center justify-between" data-testid={`cleaning-inst-${c.instance_id}`}>
                <div className="flex items-center gap-3">
                  <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${c.status === "completed" ? "bg-brand-light" : "bg-slate-100"}`}>
                    {c.status === "completed" ? <CheckCircle2 className="text-brand-dark" size={20} /> : <SprayCan className="text-slate-500" size={20} />}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800">{c.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                      <Clock size={12} /> due {c.due_time || "—"}
                      {c.photo_required && <span className="text-amber-600 flex items-center gap-0.5"><Camera size={12} /> photo</span>}
                      {c.assigned_to_name_snapshot && <span className="flex items-center gap-0.5 text-slate-600" data-testid={`cleaning-inst-assignee-${c.instance_id}`}><User size={12} /> {c.assigned_to_name_snapshot}</span>}
                      {c.completed_by_name && <span className="text-brand-dark">· done by {c.completed_by_name}</span>}
                    </div>
                  </div>
                </div>
                {c.status === "completed" ? <StatusBadge status="completed" /> :
                  has("complete_cleaning") && <Button size="sm" className="bg-brand hover:bg-brand-hover rounded-lg" data-testid={`complete-cleaning-${c.instance_id}`} onClick={() => setComplete(c)}>Complete</Button>}
              </div>
            ))}
        </TabsContent>

        {mine.linked && (
          <TabsContent value="mine" className="space-y-4" data-testid="my-cleaning-tasks">
            <div>
              <div className="text-xs font-semibold uppercase text-slate-400 mb-2">Due today</div>
              {mine.instances.length === 0 ? <EmptyState icon={CheckCircle2} title="Nothing assigned to you today." /> :
                mine.instances.map((c) => (
                  <div key={c.instance_id} className="cc-card p-4 flex items-center justify-between mb-2" data-testid={`my-cleaning-inst-${c.instance_id}`}>
                    <div><div className="font-semibold text-slate-800">{c.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2"><Clock size={12} /> due {c.due_time || "—"}{c.photo_required && <span className="text-amber-600 flex items-center gap-0.5"><Camera size={12} /> photo</span>}</div></div>
                    {c.status === "completed" ? <StatusBadge status="completed" /> :
                      has("complete_cleaning") && <Button size="sm" className="bg-brand hover:bg-brand-hover rounded-lg" data-testid={`my-complete-cleaning-${c.instance_id}`} onClick={() => setComplete(c)}>Complete</Button>}
                  </div>
                ))}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase text-slate-400 mb-2">My recurring tasks</div>
              {mine.tasks.length === 0 ? <EmptyState icon={SprayCan} title="No recurring tasks assigned to you." /> :
                mine.tasks.map((t) => (
                  <div key={t.task_id} className="cc-list-row" data-testid={`my-cleaning-task-${t.task_id}`}>
                    <div><div className="font-semibold text-slate-800">{t.name}</div>
                      <div className="text-xs text-slate-500 capitalize">{t.frequency}{t.frequency === "weekly" && ` · ${(t.weekdays || []).map((d) => WEEKDAYS[d]).join(", ")}`} · due {t.due_time || "—"}</div></div>
                    <StatusBadge status={t.priority === "high" ? "overdue" : "pending"} />
                  </div>
                ))}
            </div>
          </TabsContent>
        )}

        {["daily", "weekly", "monthly"].map((f) => (
          <TabsContent key={f} value={f} className="space-y-2">
            {has("manage_cleaning") && people.length > 0 && (
              <div className="flex items-center gap-2 mb-1">
                <Label className="text-xs text-slate-500">Assignee</Label>
                <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                  <SelectTrigger className="w-56 h-9" data-testid="cleaning-assignee-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All assignees</SelectItem>
                    <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                    {people.map((p) => <SelectItem key={p.person_id} value={p.person_id}>{p.display_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {byFreq(f).length === 0 ? <EmptyState icon={SprayCan} title={`No ${f} tasks.`} /> :
              byFreq(f).map((t) => (
                <div key={t.task_id} className="cc-list-row" data-testid={`cleaning-task-${t.task_id}`}>
                  <div><div className="font-semibold text-slate-800">{t.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                      <span>due {t.due_time || "—"}{f === "weekly" && ` · ${(t.weekdays || []).map((d) => WEEKDAYS[d]).join(", ")}`} · {t.photo_probability}% photo check</span>
                      {t.assigned_to_name_snapshot
                        ? <span className="flex items-center gap-0.5 text-slate-600" data-testid={`cleaning-task-assignee-${t.task_id}`}><User size={12} /> {t.assigned_to_name_snapshot}</span>
                        : <span className="text-slate-400" data-testid={`cleaning-task-unassigned-${t.task_id}`}>Unassigned</span>}
                    </div></div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={t.priority === "high" ? "overdue" : "pending"} />
                    {has("manage_cleaning") && <button onClick={() => setEditTask(t)} title="Edit / reassign" data-testid={`edit-cleaning-${t.task_id}`} className="text-slate-400 hover:text-brand"><Pencil size={16} /></button>}
                    {has("manage_cleaning") && <button onClick={async () => { if (!window.confirm(`Archive "${t.name}"? Completed cleaning history is preserved.`)) return; try { await api.delete(`/cleaning/tasks/${t.task_id}`); toast.success("Task archived"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed to archive"); } }} title="Archive task" data-testid={`archive-cleaning-${t.task_id}`} className="text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>}
                  </div>
                </div>
              ))}
          </TabsContent>
        ))}

        <TabsContent value="log" className="space-y-2">
          {log.length === 0 ? <EmptyState icon={Clock} title="No completed cleaning yet." /> :
            log.map((c) => (
              <div key={c.instance_id} className="cc-card p-4 flex items-center justify-between" data-testid={`cleaning-log-${c.instance_id}`}>
                <div><div className="font-semibold text-slate-800">{c.name}</div>
                  <div className="text-xs text-slate-500">
                    {c.assigned_to_name_snapshot && <span>Assigned to {c.assigned_to_name_snapshot} · </span>}
                    Completed by <TeamIdentity userId={c.completed_by} fallbackName={c.completed_by_name} size={18} testid={`cleaning-log-by-${c.instance_id}`} /> · {fmtDateTime(c.completed_at)} · {c.date}{c.completed_via === "kitchen_tablet" && " · on Kitchen Tablet"}</div>
                  {(c.assignment_history || []).length > 0 && (
                    <div className="text-xs text-slate-500 mt-0.5" data-testid={`cleaning-log-history-${c.instance_id}`}>
                      {c.original_assigned_to_name ? `Originally ${c.original_assigned_to_name}` : "Originally unassigned"}
                      {c.assignment_history.map((h, i) => <span key={i}> · {h.action === "taken_over" ? "taken over" : "taken"} by {h.by_name} {fmtDateTime(h.at)}</span>)}
                    </div>
                  )}</div>
                {c.photo_file_id && <a href={`${api.defaults.baseURL}/files/${c.photo_file_id}?auth=${localStorage.getItem("cc_token")}`} target="_blank" rel="noreferrer" className="text-brand text-sm font-medium" data-testid={`view-photo-${c.instance_id}`}>View photo</a>}
              </div>
            ))}
        </TabsContent>
      </Tabs>

      {modal && <TaskModal people={people} onClose={() => setModal(false)} onSaved={() => { setModal(false); load(); }} />}
      {editTask && <TaskModal task={editTask} people={people} onClose={() => setEditTask(null)} onSaved={() => { setEditTask(null); load(); }} />}
      {batch && <BatchAddCleaning existing={tasks.filter((t) => t.active).map((t) => t.name)} people={people} onClose={() => setBatch(false)} onDone={load} />}
      {complete && <CompleteModal inst={complete} onClose={() => setComplete(null)} onDone={() => { setComplete(null); load(); }} />}
    </div>
  );
}

export function CompleteModal({ inst, onClose, onDone }) {
  const [photo, setPhoto] = useState(null);
  const [checking, setChecking] = useState(false);
  const [verdict, setVerdict] = useState(null);   // one of RELEVANCE keys once a check resolves
  const [acked, setAcked] = useState(false);       // explicit "Complete anyway" acknowledgement
  const [submitting, setSubmitting] = useState(false);
  const reqSeq = React.useRef(0);                   // race token — ignore stale AI responses

  // Runs an advisory relevance check for the FINAL selected file_id. Any earlier in-flight response is
  // discarded via the sequence token so photo A's verdict can never land on photo B.
  const onPhotoChange = async (fileId) => {
    reqSeq.current += 1;
    const seq = reqSeq.current;
    setPhoto(fileId || null);
    setVerdict(null);       // replacing/removing a photo immediately clears the prior verdict
    setAcked(false);
    if (!fileId) { setChecking(false); return; }
    setChecking(true);
    const v = await fetchPhotoRelevance({
      fileId, taskTitle: inst.name, taskDescription: inst.instructions, siteId: inst.venue_id,
    });
    if (seq !== reqSeq.current) return;             // a newer photo superseded this check — drop it
    setVerdict(v);
    setChecking(false);
  };

  const ui = verdict ? verdictUi(verdict) : null;
  const needsAck = verdict === "CLEARLY_IRRELEVANT";
  const photoOk = !inst.photo_required || !!photo;
  const canSubmit = photoOk && !checking && !submitting && (!needsAck || acked);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post(`/cleaning/instances/${inst.instance_id}/complete`, {
        photo_file_id: photo,
        relevance_verdict: verdict || undefined,
        relevance_acknowledged: needsAck ? acked : undefined,
      });
      toast.success("Task completed");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="complete-cleaning-modal">
        <DialogHeader><DialogTitle>{inst.name}</DialogTitle></DialogHeader>
        {inst.instructions && <p className="text-sm text-slate-500">{inst.instructions}</p>}
        {inst.photo_required && (
          <div className="mt-2">
            <PhotoUpload value={photo} onChange={onPhotoChange} required label="Photo proof required" testid="cleaning-photo" />
            {checking && (
              <div className="mt-2 flex items-center gap-2 text-sm text-slate-500" data-testid="relevance-checking">
                <Loader2 size={14} className="animate-spin" /> Checking photo relevance…
              </div>
            )}
            {!checking && ui && (
              <div className="mt-2 space-y-1.5" data-testid="relevance-result">
                <div className={`text-sm font-medium flex items-center gap-1.5 ${ui.tone}`} data-testid={`relevance-verdict-${verdict}`}>
                  <Sparkles size={14} /> {ui.label}
                </div>
                {verdict === "UNCERTAIN" && <p className="text-xs text-slate-500" data-testid="relevance-note-uncertain">The AI couldn't confidently confirm this photo relates to the task. You can still complete it.</p>}
                {verdict === "UNAVAILABLE" && <p className="text-xs text-slate-500" data-testid="relevance-note-unavailable">AI photo verification is unavailable right now. This never blocks completing your task.</p>}
                {needsAck && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-2.5" data-testid="relevance-irrelevant-block">
                    <p className="text-xs text-red-700">This photo appears unrelated to the cleaning task. We recommend retaking or replacing it above. This only assesses photo relevance — it is not a cleanliness or compliance decision.</p>
                    <label className="mt-2 flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} data-testid="relevance-ack-checkbox" className="accent-brand" />
                      I've reviewed the photo and want to complete anyway
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} data-testid="confirm-complete-cleaning" disabled={!canSubmit} className="bg-brand hover:bg-brand-hover">
            {needsAck ? "Complete anyway" : "Mark complete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskModal({ task, people = [], onClose, onSaved }) {
  const isEdit = !!task;
  const [f, setF] = useState(task
    ? { name: task.name || "", frequency: task.frequency || "daily", due_time: task.due_time || "22:00",
        weekdays: task.weekdays || [], monthday: task.monthday || 1, priority: task.priority || "normal",
        instructions: task.instructions || "", photo_probability: task.photo_probability ?? 20,
        assigned_to_person_id: task.assigned_to_person_id || UNASSIGNED }
    : { name: "", frequency: "daily", due_time: "22:00", weekdays: [], monthday: 1, priority: "normal",
        instructions: "", photo_probability: 20, assigned_to_person_id: UNASSIGNED });
  const toggleDay = (i) => setF({ ...f, weekdays: f.weekdays.includes(i) ? f.weekdays.filter((d) => d !== i) : [...f.weekdays, i] });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (saving) return;
    if (!f.name) return toast.error("Name required");
    const payload = { ...f, assigned_to_person_id: f.assigned_to_person_id === UNASSIGNED ? null : f.assigned_to_person_id };
    setSaving(true);
    try {
      const { data } = isEdit
        ? await api.put(`/cleaning/tasks/${task.task_id}`, payload)
        : await api.post("/cleaning/tasks", payload);
      toast.success(isEdit ? "Cleaning task updated" : "Cleaning task created");
      if (data?.assignment_warning?.message) toast.warning(data.assignment_warning.message);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="cleaning-task-modal">
        <DialogHeader><DialogTitle>{isEdit ? "Edit cleaning task" : "New cleaning task"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold text-slate-600">Task name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cleaning-name" className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-600">Frequency</Label>
              <Select value={f.frequency} onValueChange={(v) => setF({ ...f, frequency: v })}><SelectTrigger data-testid="cleaning-freq" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["daily", "weekly", "monthly", "once"].map((x) => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs font-semibold text-slate-600">Due time</Label><Input type="time" value={f.due_time} onChange={(e) => setF({ ...f, due_time: e.target.value })} data-testid="cleaning-time" className="mt-1.5" /></div>
          </div>
          {f.frequency === "weekly" && <div className="flex gap-1.5 flex-wrap">{WEEKDAYS.map((d, i) => <button key={i} onClick={() => toggleDay(i)} data-testid={`weekday-${i}`} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${f.weekdays.includes(i) ? "bg-brand text-white" : "bg-slate-100 text-slate-600"}`}>{d}</button>)}</div>}
          <div><Label className="text-xs font-semibold text-slate-600">Assign to (optional)</Label>
            <Select value={f.assigned_to_person_id} onValueChange={(v) => setF({ ...f, assigned_to_person_id: v })}>
              <SelectTrigger data-testid="cleaning-assignee" className="mt-1.5"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                {people.map((p) => <SelectItem key={p.person_id} value={p.person_id}>{p.display_name}{p.department ? ` · ${p.department}` : ""}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><Label className="text-xs font-semibold text-slate-600">Photo verification chance: {f.photo_probability}%</Label>
            <input type="range" min="0" max="100" step="5" value={f.photo_probability} onChange={(e) => setF({ ...f, photo_probability: Number(e.target.value) })} data-testid="cleaning-photo-prob" className="w-full mt-2 accent-brand" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">Instructions</Label><Textarea value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} data-testid="cleaning-instructions" className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="save-cleaning" className="bg-brand hover:bg-brand-hover">{isEdit ? "Save changes" : "Create task"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BatchAddCleaning({ existing = [], people = [], onClose, onDone }) {
  const annotate = (rows) => markDuplicates(rows, { field: "name", existing });
  const assigneeOptions = [{ value: "", label: "Unassigned" }, ...people.map((p) => ({ value: p.person_id, label: p.display_name }))];
  const columns = [
    { key: "name", label: "Task name", placeholder: "e.g. Wipe down surfaces" },
    { key: "frequency", label: "Frequency", type: "select", default: "daily",
      options: [{ value: "daily", label: "Daily" }, { value: "weekly", label: "Weekly" }, { value: "monthly", label: "Monthly" }, { value: "once", label: "Once" }] },
    { key: "due_time", label: "Due time", inputType: "time", placeholder: "22:00" },
    { key: "priority", label: "Priority", type: "select", default: "normal",
      options: [{ value: "normal", label: "Normal" }, { value: "high", label: "High" }] },
    { key: "assigned_to_person_id", label: "Assignee", type: "select", default: "", options: assigneeOptions },
    { key: "weekdays", label: "Weekdays", placeholder: "Mon,Tue", visible: (v) => (v.frequency || "daily") === "weekly" },
    { key: "monthday", label: "Day of month", inputType: "number", placeholder: "1-31", visible: (v) => v.frequency === "monthly" },
  ];
  const bulkFields = people.length > 0 ? [{ key: "assigned_to_person_id", label: "Assign all to", options: assigneeOptions }] : [];

  const validateRow = (v) => {
    const errors = {};
    if (!String(v.name || "").trim()) errors.name = "Required";
    const freq = v.frequency || "daily";
    if (freq === "weekly" && parseWeekdays(v.weekdays).length === 0) errors.weekdays = "Pick at least one day";
    if (freq === "monthly") {
      const md = Number(v.monthday);
      if (!Number.isInteger(md) || md < 1 || md > 31) errors.monthday = "1-31";
    }
    return { errors, warnings: [] };
  };

  const onCreate = async (subset, allRows) => {
    const siteId = localStorage.getItem("cc_site") || undefined;
    const payloadRows = subset.map((r) => {
      const v = r.values;
      const freq = v.frequency || "daily";
      return {
        name: String(v.name || "").trim(),
        frequency: freq,
        due_time: v.due_time || "",
        priority: v.priority || "normal",
        assigned_to_person_id: v.assigned_to_person_id || null,
        weekdays: freq === "weekly" ? parseWeekdays(v.weekdays) : [],
        monthday: freq === "monthly" ? Number(v.monthday) : null,
        venue_id: siteId,
      };
    });
    const operation_id = (crypto.randomUUID && crypto.randomUUID()) || `op_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    try {
      const { data } = await api.post("/cleaning/tasks/batch", { operation_id, rows: payloadRows });
      const next = reconcile(allRows, subset, data.results || []);
      const summary = `${data.created} created` + (data.duplicates ? `, ${data.duplicates} duplicate` : "") + (data.failed ? `, ${data.failed} failed` : "");
      if (data.created > 0) { toast.success(`Batch: ${summary}`); onDone && onDone(); }
      else toast.message(`Batch: ${summary}`);
      return next;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Batch failed");
      return null;
    }
  };

  return (
    <BatchEditor
      columns={columns} validateRow={validateRow} annotate={annotate} pasteColumn="name" bulkFields={bulkFields}
      aliasMap={{ name: ["task", "task name", "name"], frequency: ["frequency", "freq"], due_time: ["due time", "time"], priority: ["priority"] }}
      destinationLabel="Cleaning tasks" createLabel="Create tasks" onCreate={onCreate} onClose={onClose} />
  );
}
